import {
  Action,
  Card,
  closeDialog,
  DataStatus,
  openDialog,
  showNotification,
} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback, useState} from 'react';

import {head, isEmpty, isNil, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {
  ActiveFolder,
  DmsDataGrid,
  catchUnhandledDataGridActions,
  logError,
  useWarehouseDataGridTreeFolder,
} from '@dms/shared';
import {LabourCatalogItemFrom} from '@dms/shared-aftersales';
import {LabourFolderTree} from '@dms/warehouse/shared-components';

import {TestIdProps, buildArray, suffixTestId, useLatestValue} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {LABOUR_CATALOG_GRID_CODE} from './LABOUR_CATALOG_GRID_CODE';

interface LabourListProps extends TestIdProps {
  directSaleId: string;
  hasPendingRequest: boolean;
  onAddItemsToBasket: (labourCatalogIds: string[]) => Promise<void>;
}

const PREDEFINED_COLUMNS = ['itemName'] as const;
const CREATE_NEW_LABOUR_DIALOG = 'create-new-labour-dialog';

export function RequestTabLabourList(props: LabourListProps) {
  const [folderTreeRef, dataGridModifier] = useWarehouseDataGridTreeFolder();
  const [activeCatalogueFolder, setActiveCatalogueFolder] = useState<ActiveFolder>();

  const hasPendingRequestRef = useLatestValue(props.hasPendingRequest);

  const handleCreateNew = () => {
    const closeCreateNewDialog = () => closeDialog(CREATE_NEW_LABOUR_DIALOG);

    openDialog(
      <LabourCatalogItemFrom
        onClose={closeCreateNewDialog}
        onSubmitted={closeCreateNewDialog}
        freePositionData={{kind: 'directSale', directSaleId: props.directSaleId}}
        catalogueFolder={activeCatalogueFolder}
        data-testid={suffixTestId('createNewLabour', props)}
      />,
      {
        id: CREATE_NEW_LABOUR_DIALOG,
        title: i18n.t('general.labels.createNew'),
        scrollBehavior: 'outside',
      }
    );
  };

  const cardActions = buildArray<Action>().add({
    type: 'button',
    variant: 'link',
    leftIcon: 'content/add_circle',
    title: i18n.t('general.labels.createNew'),
    onClick: handleCreateNew,
    'data-testid': suffixTestId('createNewLabourAction', props),
  });

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
    refreshData,
    deselectAll,
  }) => {
    if (hasPendingRequestRef.current) {
      showNotification.warning(i18n.t('entity.warehouse.labels.thereIsAnotherOngoingAction'));
      return;
    }

    const labourCatalogIds = isArray(rowId) ? rowId : [rowId];

    if (isEmpty(labourCatalogIds)) {
      return logError('No rows selected');
    }

    return match(actionKey)
      .with('addToBasket', 'add', () =>
        props.onAddItemsToBasket(labourCatalogIds).then(() => {
          deselectAll();
          refreshData();
        })
      )
      .with('edit', () => {
        const LABOUR_CATALOG_ITEM_DIALOG = 'edit-catalog-item-dialog';

        const labourCatalogId = head(labourCatalogIds);

        if (isNil(labourCatalogId)) {
          return logError('Row id is not defined');
        }

        const itemName = getColumnData<string>('itemName')?.value;

        openDialog(
          <LabourCatalogItemFrom
            editedId={labourCatalogId}
            onSubmitted={refreshData}
            onClose={() => closeDialog(LABOUR_CATALOG_ITEM_DIALOG)}
          />,
          {
            id: LABOUR_CATALOG_ITEM_DIALOG,
            title: itemName,
          }
        );
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => {
      const modifiedFilter = mergeAll([filter, {directSaleId: props.directSaleId}]);

      return dataGridModifier ? dataGridModifier?.queryModifier(modifiedFilter) : modifiedFilter;
    },
    [props.directSaleId, dataGridModifier]
  );

  return (
    <Card title={i18n.t('entity.warehouse.labels.itemsList')} actions={cardActions} isFullHeight>
      <HStack height="100%" spacing={4}>
        <Box
          flex={1}
          borderRight="1px solid"
          borderColor="palettes.neutral.40.100"
          paddingRight={4}
          height="100%"
          overflowY="auto"
        >
          <DataStatus>
            <VStack spacing={1} height="100%">
              <LabourFolderTree
                ref={folderTreeRef}
                directSaleId={props.directSaleId}
                onActiveFolderChange={setActiveCatalogueFolder}
                data-testid={suffixTestId('articleFolderTree', props)}
              />
            </VStack>
          </DataStatus>
        </Box>

        <Box flex={3}>
          <DmsDataGrid
            // DG must be re-rendered on folder change to update the query modifier
            // eslint-disable-next-line eslint-js/no-restricted-syntax
            key={dataGridModifier?.key}
            gridCode={LABOUR_CATALOG_GRID_CODE}
            autoFocusSmartSearch
            actionCallback={actionCallback}
            predefinedColumns={PREDEFINED_COLUMNS}
            queryModifier={queryModifier}
            data-testid={suffixTestId('datagrid', props)}
          />
        </Box>
      </HStack>
    </Card>
  );
}
